import { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const KEEP = [
  ["keep_catalog", "Keep products & raw materials (recipes and prices; stock is set to 0)"],
  ["keep_settings", "Keep settings (name, logo, UPI/PhonePe, time slots, WhatsApp toggles)"],
  ["keep_staff", "Keep all staff logins (otherwise only the owner login stays)"],
];

export const ResetDataCard = () => {
  const [open, setOpen] = useState(false);
  const [keep, setKeep] = useState({ keep_catalog: true, keep_settings: true, keep_staff: false });
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    setBusy(true);
    try {
      await api.post("/admin/reset", { confirm, ...keep });
      toast.success("Business data cleared. Starting fresh.");
      setTimeout(() => window.location.assign("/"), 800);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="mt-8 max-w-2xl rounded-3xl border border-rose-200 bg-rose-50/50 p-6" data-testid="reset-data-card">
      <div className="flex items-center gap-2 font-display text-lg font-semibold text-rose-800"><AlertTriangle className="h-5 w-5" />Danger zone</div>
      <p className="mt-1 text-sm text-rose-900/80">Delete all customers, orders, deliveries, bills & payments, UTR claims, ratings, referrals, sign-ups, production, stock, alerts and WhatsApp logs. This cannot be undone.</p>
      <Button variant="outline" className="mt-4 rounded-full border-rose-300 text-rose-700 hover:bg-rose-100" onClick={() => setOpen(true)} data-testid="reset-data-open-btn">Reset business data</Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); setConfirm(""); }}>
        <DialogContent className="max-w-md" data-testid="reset-data-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-rose-800">Reset business data?</DialogTitle>
            <DialogDescription>All business records will be permanently deleted from this app. Demo data will not come back.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {KEEP.map(([k, l]) => (
              <label key={k} className="flex items-start gap-3 text-sm">
                <Checkbox checked={keep[k]} onCheckedChange={(v) => setKeep({ ...keep, [k]: !!v })} data-testid={`reset-${k}`} />
                <span>{l}</span>
              </label>
            ))}
            <div className="space-y-1 pt-2">
              <div className="text-sm font-semibold">Type <span className="font-mono text-rose-700">RESET</span> to confirm</div>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="RESET" data-testid="reset-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={confirm !== "RESET" || busy} className="bg-rose-600 hover:bg-rose-700" onClick={reset} data-testid="reset-confirm-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete everything"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
